// Main app — composes layout, filters, ingestion, dark mode, and the four views.
//
// The dashboard no longer holds raw rows in the browser. CSV log files are
// uploaded to the backend, parsed + detected + rolled up server-side, and every
// view reads pre-aggregated, server-filtered data from the analytics API. Filter
// options and audience counts come from /analytics/meta; the catalogue (used for
// client-side pricing) is still fetched once at startup.

import React from 'react';
import { createRoot } from 'react-dom/client';
import { setCatalogue, getProducts } from './catalogue.jsx';
import { api, setCsrfToken, setActiveOrgId } from './api.jsx';
import { Icon, css, MultiSelect, Empty, Loading, fmtNum } from './ui.jsx';
import { useTweaks, TweaksPanel, TweakSection, TweakColor, TweakRadio, TweakToggle } from './tweaks-panel.jsx';

const { useState: useS, useEffect: useE, useMemo: useM, useRef: useR, useCallback: useC, lazy, Suspense } = React;

// Code-split the heavier surfaces so the initial bundle stays small. The
// analytics views pull in Chart.js; the Catalogue and Contracts admin modules
// are large and only opened on demand. Each loads via dynamic import() the first
// time its tab is shown, behind the <Suspense> fallback below.
const OverviewView     = lazy(() => import('./views.jsx').then(m => ({ default: m.OverviewView })));
const BySchoolView     = lazy(() => import('./views.jsx').then(m => ({ default: m.BySchoolView })));
const ByProductView    = lazy(() => import('./views.jsx').then(m => ({ default: m.ByProductView })));
const SpendVsUsageView = lazy(() => import('./views.jsx').then(m => ({ default: m.SpendVsUsageView })));
const DataTableView    = lazy(() => import('./views.jsx').then(m => ({ default: m.DataTableView })));
const CatalogueAdmin   = lazy(() => import('./catalogue-admin.jsx').then(m => ({ default: m.CatalogueAdmin })));
const ContractsModule  = lazy(() => import('./contracts.jsx').then(m => ({ default: m.ContractsModule })));
const AdminPanel       = lazy(() => import('./admin.jsx').then(m => ({ default: m.AdminPanel })));

function Logo({ src }) {
  return (
    <img className="brand-mark" src={src || 'logo.jpg'} alt="SaaS Monitor logo" style={{ borderRadius: 7, objectFit: 'cover' }} />
  );
}

const TABS = [
  { id: 'overview', label: 'Overview', icon: 'layout-dashboard' },
  { id: 'school',   label: 'By school', icon: 'school' },
  { id: 'product',  label: 'By product', icon: 'package' },
  { id: 'spend',    label: 'Spend vs usage', icon: 'scale' },
  { id: 'data',     label: 'Data table', icon: 'table-2' },
];

function fmtDateStr(d) {
  if (!d) return null;
  const dt = new Date(String(d) + 'T00:00:00');
  if (isNaN(dt)) return String(d);
  return dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function App({ session, onLogout }) {
  const isAdmin = session.role === 'system_admin' || session.role === 'org_admin';
  const isSystemAdmin = session.role === 'system_admin';
  const isDemo = !!session.isDemo;
  // What each role may do in the UI (the server enforces all of this too):
  //  • Imports: real admins only (never demo — demo is sample-data only).
  //  • Catalogue: admins edit; demo and viewers see it read-only and can run
  //    AI research (org credits for non-demo; fixed weekly allowance for demo).
  //  • Contracts: everyone sees it, but only real admins can edit.
  //  • Admin (users/orgs): admins only.
  const canImports = isAdmin && !isDemo;
  // All users now see the Catalogue tab: admins edit it, demo and viewers are read-only.
  const canCatalogue = isAdmin || isDemo || session.role === 'viewer';
  const catalogueReadOnly = !isAdmin; // demo and plain viewers are read-only
  // All authenticated users may reach the research panel; the server enforces
  // each role's credit allowance.
  const allowResearch = true;
  const contractsReadOnly = !isAdmin;

  const TWEAK_DEFAULTS = /*EDITMODE-BEGIN*/{
    "primaryColor": "#1F8FCB",
    "density": "regular",
    "showLogoColors": true,
    "hideNoiseDefault": true
  }/*EDITMODE-END*/;
  const [tw, setTweak] = useTweaks(TWEAK_DEFAULTS);
  useE(() => {
    document.documentElement.style.setProperty('--teal', tw.primaryColor);
  }, [tw.primaryColor]);
  useE(() => {
    const map = { compact: '6px 10px', regular: '11px 14px', comfy: '14px 18px' };
    const cssText = `table.dt td { padding: ${map[tw.density] || map.regular} !important; }`;
    let el = document.getElementById('twk-density');
    if (!el) { el = document.createElement('style'); el.id = 'twk-density'; document.head.appendChild(el); }
    el.textContent = cssText;
  }, [tw.density]);

  const [tab, setTab] = useS('overview');
  const [dark, setDark] = useS(false);
  const [tweaksOpen, setTweaksOpen] = useS(false);
  const [drag, setDrag] = useS(false);

  // Session-only temporary logo: lives in sessionStorage (cleared on tab close / logout).
  // NOT stored through setTweak/EDITMODE — that persists to disk and must not carry base64 images.
  const [sessionLogo, setSessionLogoState] = useS(() => {
    try { return sessionStorage.getItem('session_logo') || null; } catch { return null; }
  });
  const setSessionLogo = useC((val) => {
    setSessionLogoState(val);
    try {
      if (val) sessionStorage.setItem('session_logo', val);
      else sessionStorage.removeItem('session_logo');
    } catch { /* ignore quota/security errors */ }
  }, []);
  const [sessionLogoErr, setSessionLogoErr] = useS(null);

  // Org switcher (system_admin only). null = "All organisations" (no org filter
  // server-side). Other admins/viewers are always scoped to their own org and
  // never see this control. Changing it re-fetches all analytics/meta.
  const [orgList, setOrgList] = useS([]);
  const [activeOrg, setActiveOrg] = useS(null); // null = all orgs
  useE(() => {
    if (!isSystemAdmin) return;
    api.getOrgs().then(setOrgList).catch(() => {});
  }, [isSystemAdmin]);
  useE(() => { setActiveOrgId(isSystemAdmin ? activeOrg : null); }, [isSystemAdmin, activeOrg]);

  // Resolved branding values for the app shell.
  // activeOrgName: the org the user is currently viewing (for subtitle).
  const activeOrgEntry = useM(() => activeOrg != null ? orgList.find(o => o.id === activeOrg) || null : null, [orgList, activeOrg]);
  const activeOrgName = isSystemAdmin
    ? (activeOrgEntry?.name || null)
    : (session.organisationName || null);
  const activeOrgLogo = isSystemAdmin
    ? (activeOrgEntry?.logo || null)
    : (session.orgLogo || null);
  // Logo precedence: session temporary → org permanent → default
  const resolvedLogoSrc = sessionLogo || activeOrgLogo || null;

  // Deep-links from the Spend vs usage view: jump to a product's usage breakdown,
  // or to the Contracts module (open a linked contract / start one for a product).
  const [productFocus, setProductFocus] = useS(null);
  const [contractsIntent, setContractsIntent] = useS(null);
  const onViewUsage = useC((name) => { setProductFocus(name); setTab('product'); }, []);
  const onOpenContract = useC((p) => {
    setContractsIntent({ type: 'open', contractIds: p.contractIds || [], productName: p.name });
    setTab('contracts');
  }, []);
  const onAddContract = useC((p) => {
    const match = getProducts().find((cp) => cp.name === p.name);
    setContractsIntent({ type: 'create', productId: match ? match.id : null, productName: p.name });
    setTab('contracts');
  }, []);

  // Catalogue (data-driven SaaS lookup / pricing) loaded from the API at startup.
  const [catReady, setCatReady] = useS(false);
  const [catError, setCatError] = useS(null);

  // Server-side data state.
  const [meta, setMeta] = useS(null);
  const [batches, setBatches] = useS([]);
  const [uploading, setUploading] = useS(false);
  const [uploadInfo, setUploadInfo] = useS(null);
  const [renewals, setRenewals] = useS(null);

  const loadCatalogue = useC(async () => {
    setCatError(null);
    const data = await api.getCatalogue();
    setCatalogue(data);
    setCatReady(true);
  }, []);

  useE(() => { loadCatalogue().catch(e => setCatError(e.message)); }, [loadCatalogue]);

  const refreshMeta = useC(async () => {
    try { setMeta(await api.getMeta()); } catch { /* keep previous meta */ }
  }, []);
  const refreshBatches = useC(async () => {
    try { setBatches(await api.getBatches()); } catch { /* keep previous */ }
  }, []);
  const refreshRenewals = useC(async () => {
    try { setRenewals(await api.getRenewals()); } catch { /* keep previous */ }
  }, []);

  useE(() => {
    if (!catReady) return;
    refreshMeta();
    refreshBatches();
    refreshRenewals();
  }, [catReady, activeOrg, refreshMeta, refreshBatches, refreshRenewals]);

  // Poll while any batch is still being processed; refresh meta once it settles.
  useE(() => {
    if (!catReady) return undefined;
    const active = batches.some(b => b.status === 'processing' || b.status === 'pending');
    if (!active) return undefined;
    const t = setInterval(async () => {
      const list = await api.getBatches().catch(() => null);
      if (!list) return;
      setBatches(list);
      if (!list.some(b => b.status === 'processing' || b.status === 'pending')) refreshMeta();
    }, 1500);
    return () => clearInterval(t);
  }, [batches, catReady, refreshMeta]);

  // After catalogue admin edits, refetch the catalogue so client-side pricing
  // reflects the change. (Detection/noise are baked at ingest time, so existing
  // rollups are not retroactively re-detected.)
  const reloadCatalogue = useC(async () => {
    const data = await api.getCatalogue();
    setCatalogue(data);
  }, []);

  // Filters
  const [dateFrom, setDateFrom] = useS('');
  const [dateTo, setDateTo] = useS('');
  const [schoolSel, setSchoolSel] = useS(new Set());
  const [catSel, setCatSel] = useS(new Set());
  const [statusSel, setStatusSel] = useS(new Set());
  const [hideNoise, setHideNoise] = useS(true);
  const [onlyCatalogued, setOnlyCatalogued] = useS(false);
  const [audience, setAudience] = useS('all'); // 'all' | 'staff' | 'student'
  useE(() => { setHideNoise(tw.hideNoiseDefault); }, [tw.hideNoiseDefault]);

  useE(() => { document.documentElement.classList.toggle('dark', dark); }, [dark]);

  // Filter options + counts come from the backend meta endpoint.
  const allSchools = meta?.schools || [];
  const allCats    = meta?.categories || [];
  const allStatus  = meta?.statuses || [];
  const total        = meta?.total || 0;
  // Audience figures are now distinct PEOPLE (unique users), not request volume.
  // `peopleCount` is the combined distinct across both audiences, so it is not
  // staffCount + studentCount (anyone in both files is counted once).
  const staffCount   = meta?.staff || 0;
  const studentCount = meta?.student || 0;
  const peopleCount  = meta?.people || 0;
  const noiseCount   = meta?.noise || 0;
  const hasData = total > 0;
  // Whether any loaded import batch is the built-in sample data. Gates the
  // sidebar's "Clear sample data" action (which removes only those batches).
  const hasSampleData = useM(() => batches.some((b) => b.is_sample), [batches]);

  // The filter object every view + the data table send to the API.
  const filters = useM(() => ({
    from: dateFrom || undefined,
    to: dateTo || undefined,
    audience: audience !== 'all' ? audience : undefined,
    schools: [...schoolSel],
    categories: [...catSel],
    statuses: [...statusSel],
    hideNoise: hideNoise || undefined,
    onlyCatalogued: onlyCatalogued || undefined,
  }), [dateFrom, dateTo, audience, schoolSel, catSel, statusSel, hideNoise, onlyCatalogued]);

  // ── Ingestion ────────────────────────────────────────────────────────────────
  const uploadFiles = useC(async (fileList, aud) => {
    const files = [...fileList];
    if (!files.length) return;
    setUploading(true);
    setUploadInfo(null);
    try {
      const done = [];
      for (const f of files) {
        const r = await api.uploadCSV(f, aud);
        done.push({ name: f.name, duplicate: r.duplicate, message: r.message });
      }
      const dupes = done.filter(d => d.duplicate).length;
      setUploadInfo({
        ok: true,
        text: dupes
          ? `${done.length} file${done.length > 1 ? 's' : ''} · ${dupes} already imported`
          : `${done.length} file${done.length > 1 ? 's' : ''} importing…`,
      });
      if (aud) setAudience(aud);
      await refreshBatches();
      await refreshMeta();
    } catch (e) {
      setUploadInfo({ ok: false, text: e.message });
    } finally {
      setUploading(false);
    }
  }, [refreshBatches, refreshMeta]);

  const loadSample = useC(async () => {
    setUploading(true);
    setUploadInfo(null);
    try {
      await api.ingestSample();
      setUploadInfo({ ok: true, text: 'Sample data importing…' });
      await refreshBatches();
      await refreshMeta();
    } catch (e) {
      setUploadInfo({ ok: false, text: e.message });
    } finally {
      setUploading(false);
    }
  }, [refreshBatches, refreshMeta]);

  const deleteBatch = useC(async (id) => {
    await api.deleteBatch(id);
    await refreshBatches();
    await refreshMeta();
  }, [refreshBatches, refreshMeta]);

  // "Clear sample data" removes only the built-in sample batches (and their
  // cascaded events/rollups). Real uploaded CSVs are removed deliberately, one
  // at a time, from the Imports module instead.
  const clearData = useC(async () => {
    if (!window.confirm('Clear the loaded sample data? This removes the sample import batches and their events. Your uploaded CSVs are not affected.')) return;
    setUploading(true);
    try {
      await api.clearSampleData();
      await refreshBatches();
      await refreshMeta();
      setUploadInfo(null);
      setSchoolSel(new Set()); setCatSel(new Set()); setStatusSel(new Set());
      setDateFrom(''); setDateTo(''); setAudience('all');
    } finally {
      setUploading(false);
    }
  }, [refreshBatches, refreshMeta]);

  const resetFilters = () => {
    setSchoolSel(new Set()); setCatSel(new Set()); setStatusSel(new Set());
    setDateFrom(''); setDateTo('');
  };
  const filtersActive = schoolSel.size || catSel.size || statusSel.size || dateFrom || dateTo;

  // Keep the active tab valid for this role — if a user lands on (or is left on)
  // a tab they may not access, fall back to Overview. This MUST run before the
  // catalogue-loading early return below, otherwise the hook is skipped while
  // loading and then runs once the catalogue arrives — changing the hook count
  // between renders ("Rendered more hooks than during the previous render"),
  // which crashes the whole app to a blank page right after login.
  useE(() => {
    if (tab === 'imports' && !canImports) setTab('overview');
    else if (tab === 'catalogue' && !canCatalogue) setTab('overview');
    else if (tab === 'admin' && !isAdmin) setTab('overview');
  }, [tab, canImports, canCatalogue, isAdmin]);

  if (!catReady) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)' }}>
        {catError ? (
          <Empty
            icon="alert-triangle"
            title="Couldn't load the SaaS catalogue"
            sub={catError}
            action={<button className="btn btn-primary" onClick={() => loadCatalogue().catch(e => setCatError(e.message))}>Retry</button>}
          />
        ) : (
          <Loading label="Loading SaaS catalogue…" />
        )}
      </div>
    );
  }

  const isManage = tab === 'catalogue' || tab === 'imports' || tab === 'contracts' || tab === 'admin';

  return (
    <div className="app">
      {/* Sidebar */}
      <aside className="sidebar">
        <div className="sidebar-head">
          <Logo src={resolvedLogoSrc} />
          <div>
            <div className="brand-name">SaaS Monitor</div>
            <div className="brand-sub">{activeOrgName || 'Trust analytics'}</div>
          </div>
        </div>
        <nav className="nav">
          <div className="nav-section">Analyse</div>
          {TABS.map(t => (
            <div key={t.id} className={css('nav-item', tab === t.id && 'active')} onClick={() => setTab(t.id)}>
              <Icon name={t.icon} /> {t.label}
            </div>
          ))}

          <div className="nav-section" style={{ marginTop: 8 }}>Manage</div>
          {canImports && (
            <div className={css('nav-item', tab === 'imports' && 'active')} onClick={() => setTab('imports')}>
              <Icon name="database" /> Imports
              {batches.length > 0 && <span className="mono nav-tag">{batches.length}</span>}
            </div>
          )}
          {canCatalogue && (
            <div className={css('nav-item', tab === 'catalogue' && 'active')} onClick={() => setTab('catalogue')}>
              <Icon name="boxes" /> Catalogue
            </div>
          )}
          <div className={css('nav-item', tab === 'contracts' && 'active')} onClick={() => setTab('contracts')}>
            <Icon name="file-text" /> Contracts
            {renewals && (renewals.counts.overdue + renewals.counts.within90) > 0 && (
              <span
                className="mono nav-tag"
                title={`${renewals.counts.overdue} overdue · ${renewals.counts.within90} due within 90 days`}
                style={{
                  background: renewals.counts.overdue ? 'var(--red)' : 'var(--amber)',
                  color: '#fff',
                }}
              >
                {renewals.counts.overdue + renewals.counts.within90}
              </span>
            )}
          </div>
          {isAdmin && (
            <div className={css('nav-item', tab === 'admin' && 'active')} onClick={() => setTab('admin')}>
              <Icon name="shield" /> {isSystemAdmin ? 'Users & orgs' : 'Users'}
            </div>
          )}

          <div className="nav-section" style={{ marginTop: 8 }}>Audience</div>
          <div className="aud-seg">
            {[
              { id: 'all', label: 'All', icon: 'users-round', count: peopleCount },
              { id: 'staff', label: 'Staff', icon: 'briefcase', count: staffCount },
              { id: 'student', label: 'Student', icon: 'graduation-cap', count: studentCount },
            ].map(a => (
              <button
                key={a.id}
                className={css('aud-seg-btn', audience === a.id && 'active')}
                onClick={() => setAudience(a.id)}
                disabled={!hasData}
                title={`${a.label} — ${fmtNum(a.count)} people`}
              >
                <Icon name={a.icon} size={14} />
                <span>{a.label}</span>
                {hasData && <span className="aud-seg-count">{fmtNum(a.count)}</span>}
              </button>
            ))}
          </div>

          {canImports && (
            <>
              <div className="nav-section" style={{ marginTop: 8 }}>Data</div>
              <label className="nav-item" style={{ cursor: 'pointer' }}>
                <Icon name="briefcase" /> Upload staff CSV
                {staffCount > 0 && <span className="mono nav-tag">{fmtNum(staffCount)}</span>}
                <input type="file" accept=".csv" multiple style={{ display: 'none' }} onChange={e => { uploadFiles(e.target.files, 'staff'); e.target.value = ''; }} />
              </label>
              <label className="nav-item" style={{ cursor: 'pointer' }}>
                <Icon name="graduation-cap" /> Upload student CSV
                {studentCount > 0 && <span className="mono nav-tag">{fmtNum(studentCount)}</span>}
                <input type="file" accept=".csv" multiple style={{ display: 'none' }} onChange={e => { uploadFiles(e.target.files, 'student'); e.target.value = ''; }} />
              </label>
              <div className="nav-item" onClick={loadSample}><Icon name="sparkles" /> Load sample data</div>
              {hasSampleData && <div className="nav-item" onClick={clearData}><Icon name="trash-2" /> Clear sample data</div>}
            </>
          )}
        </nav>
        <div className="sidebar-foot">
          <Icon name="info" />
          <span>{hasData ? `${fmtNum(total)} events stored` : 'No data loaded'}</span>
        </div>
      </aside>

      {/* Main */}
      <main className="main">
        <div className="topbar">
          <div className="topbar-title">
            {tab === 'catalogue' ? 'Catalogue' : tab === 'imports' ? 'Imports' : tab === 'contracts' ? 'Contracts' : tab === 'admin' ? (isSystemAdmin ? 'Users & organisations' : 'Users') : (TABS.find(t => t.id === tab)?.label || 'Overview')}
          </div>
          {!isManage && hasData && (
            <span className="chip" style={{ marginLeft: 8, textTransform: 'capitalize' }}>
              <Icon name={audience === 'student' ? 'graduation-cap' : audience === 'staff' ? 'briefcase' : 'users-round'} size={11} /> {audience === 'all' ? 'All users' : audience}
            </span>
          )}
          {uploading && <span className="chip chip-teal" style={{ marginLeft: 4 }}><span className="spinner" style={{ width: 11, height: 11 }} /> Importing…</span>}
          {!uploading && uploadInfo && (
            <span className={css('chip', uploadInfo.ok ? 'chip-teal' : 'chip-red')} style={{ marginLeft: 4 }}>
              <Icon name={uploadInfo.ok ? 'check-circle-2' : 'alert-triangle'} size={11} /> {uploadInfo.text}
            </span>
          )}
          {!isManage && hasData && meta?.dateMin && (
            <span className="chip" style={{ marginLeft: 4 }} title="Date range covered by the stored data">
              <Icon name="calendar-range" size={11} />
              {fmtDateStr(meta.dateMin) === fmtDateStr(meta.dateMax)
                ? fmtDateStr(meta.dateMin)
                : `${fmtDateStr(meta.dateMin)} → ${fmtDateStr(meta.dateMax)}`}
            </span>
          )}
          <div className="topbar-spacer" />
          {isSystemAdmin && (
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginRight: 4 }} title="View data for one organisation, or all of them">
              <Icon name="building-2" size={14} />
              <select
                className="select"
                value={activeOrg ?? ''}
                onChange={e => setActiveOrg(e.target.value ? Number(e.target.value) : null)}
                style={{ minWidth: 150 }}
              >
                <option value="">All organisations</option>
                {orgList.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </label>
          )}
          <button className="btn btn-ghost btn-icon" onClick={() => setTweaksOpen(true)} title="Settings">
            <Icon name="wrench" />
          </button>
          <button className="btn btn-ghost btn-icon" onClick={() => setDark(d => !d)} title="Toggle theme">
            <Icon name={dark ? 'sun' : 'moon'} />
          </button>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginLeft: 6, paddingLeft: 10, borderLeft: '1px solid var(--border)' }}>
            <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.25, textAlign: 'right' }} title={`${session.email} · ${session.role}${session.organisationName ? ' · ' + session.organisationName : ''}`}>
              <span style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--ink)' }}>{session.email}</span>
              <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                {session.role === 'system_admin' ? 'System admin' : session.role === 'org_admin' ? 'Org admin' : 'Viewer'}
                {isDemo ? ' · Demo' : session.organisationName ? ` · ${session.organisationName}` : ''}
              </span>
            </span>
            <button className="btn btn-ghost btn-icon" onClick={onLogout} title="Sign out">
              <Icon name="log-out" />
            </button>
          </div>
        </div>

        {!isManage && hasData && (
          <div className="filters-bar">
            <span style={{ fontSize: 12, color: 'var(--ink-3)', display: 'inline-flex', alignItems: 'center', gap: 6 }}><Icon name="filter" /> Filters</span>
            <input className="input" type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} title="From" />
            <span style={{ color: 'var(--ink-3)', fontSize: 12 }}>→</span>
            <input className="input" type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} title="To" />
            <MultiSelect label="Schools" options={allSchools} selected={schoolSel} onChange={setSchoolSel} />
            <MultiSelect label="Categories" options={allCats} selected={catSel} onChange={setCatSel} />
            <MultiSelect label="Status" options={allStatus} selected={statusSel} onChange={setStatusSel} />
            <button
              className="btn btn-sm"
              onClick={() => setHideNoise(v => !v)}
              title="Hide analytics, ad, CDN and telemetry domains so the dashboard stays focused on real SaaS products"
              style={hideNoise ? { background: 'var(--teal-tint)', borderColor: 'var(--teal)', color: 'var(--teal-2)' } : null}
            >
              <Icon name={hideNoise ? 'filter' : 'filter-x'} />
              {hideNoise ? 'Hiding noise' : 'Showing all'}
              {noiseCount > 0 && <span className="mono" style={{ opacity: 0.7, marginLeft: 4 }}>{fmtNum(noiseCount)}</span>}
            </button>
            <button
              className="btn btn-sm"
              onClick={() => setOnlyCatalogued(v => !v)}
              title="Only show domains that match the built-in SaaS catalogue"
              style={onlyCatalogued ? { background: 'var(--teal-tint)', borderColor: 'var(--teal)', color: 'var(--teal-2)' } : null}
            >
              <Icon name="boxes" /> Catalogued only
            </button>
            <div style={{ flex: 1 }} />
            <span style={{ fontSize: 12, color: 'var(--ink-3)' }} className="mono">{fmtNum(total)} events</span>
            {filtersActive ? (
              <button className="btn btn-sm btn-ghost" onClick={resetFilters}><Icon name="x" /> Reset</button>
            ) : null}
          </div>
        )}

        <div className="content">
          <div className="content-inner">
            <Suspense fallback={<Loading label="Loading…" />}>
            {tab === 'catalogue' && canCatalogue && <CatalogueAdmin onChanged={reloadCatalogue} onRescanned={() => { refreshMeta(); refreshBatches(); }} readOnly={catalogueReadOnly} allowResearch={allowResearch} orgCredits={isSystemAdmin ? null : (session.orgResearchCredits ?? null)} demoQuota={isDemo ? { remaining: session.demoCreditsRemaining, resetsAt: session.demoCreditsResetAt } : null} isSystemAdmin={isSystemAdmin} isOrgAdmin={isAdmin && !isSystemAdmin} />}
            {tab === 'contracts' && <ContractsModule onChanged={refreshRenewals} intent={contractsIntent} onIntentHandled={() => setContractsIntent(null)} readOnly={contractsReadOnly} />}
            {tab === 'admin' && isAdmin && <AdminPanel session={session} />}
            {tab === 'imports' && canImports && (
              <ImportsView
                batches={batches}
                uploading={uploading}
                onUpload={uploadFiles}
                onSample={loadSample}
                onDelete={deleteBatch}
                onRefresh={() => { refreshBatches(); refreshMeta(); }}
              />
            )}
            {!isManage && !hasData && canImports && (
              <UploadFirstScreen
                drag={drag}
                onDragOver={e => { e.preventDefault(); setDrag(true); }}
                onDragLeave={() => setDrag(false)}
                onDrop={e => { e.preventDefault(); setDrag(false); uploadFiles(e.dataTransfer.files, 'staff'); }}
                onUpload={(files, aud) => uploadFiles(files, aud)}
                onSample={loadSample}
              />
            )}
            {!isManage && !hasData && !canImports && (
              <div style={{ maxWidth: 520, margin: '60px auto' }}>
                <Empty icon="database" title="No data yet" sub="There's no analytics data in your organisation yet. An administrator needs to import web-filter exports before anything shows here." />
              </div>
            )}
            {!isManage && hasData && (
              <>
                {tab === 'overview' && <OverviewView filters={filters} renewals={renewals} onManageContracts={() => setTab('contracts')} />}
                {tab === 'school'   && <BySchoolView  filters={filters} />}
                {tab === 'product'  && <ByProductView filters={filters} initialProduct={productFocus} />}
                {tab === 'spend'    && <SpendVsUsageView filters={filters} onViewUsage={onViewUsage} onOpenContract={onOpenContract} onAddContract={onAddContract} />}
                {tab === 'data'     && <DataTableView filters={filters} />}
              </>
            )}
            </Suspense>
          </div>
        </div>

        {/* Mobile bottom nav */}
        <div className="mobile-nav">
          {TABS.map(t => (
            <div key={t.id} className={css('mobile-nav-item', tab === t.id && 'active')} onClick={() => setTab(t.id)}>
              <Icon name={t.icon} />
              <span>{t.label.split(' ')[0]}</span>
            </div>
          ))}
        </div>
      </main>

      <TweaksPanel open={tweaksOpen} onOpenChange={setTweaksOpen}>
        <TweakSection label="Theme" />
        <TweakColor  label="Primary color"  value={tw.primaryColor} onChange={(v) => setTweak('primaryColor', v)} />
        <TweakRadio  label="Density"        value={tw.density}      options={['compact','regular','comfy']} onChange={(v) => setTweak('density', v)} />
        <TweakSection label="Filtering" />
        <TweakToggle label="Hide noise by default" value={tw.hideNoiseDefault} onChange={(v) => setTweak('hideNoiseDefault', v)} />
        <TweakSection label="Branding" />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {sessionLogo && (
              <img src={sessionLogo} alt="Session logo preview"
                style={{ width: 32, height: 32, objectFit: 'contain', borderRadius: 6, border: '.5px solid rgba(0,0,0,.1)', background: 'rgba(255,255,255,.6)', flexShrink: 0 }} />
            )}
            <label style={{ flex: 1, display: 'inline-flex', alignItems: 'center', gap: 6, height: 26, padding: '0 10px',
              border: '.5px solid rgba(0,0,0,.1)', borderRadius: 7, background: 'rgba(255,255,255,.6)',
              cursor: 'default', fontSize: 11.5, color: 'rgba(41,38,27,.72)' }}>
              <span style={{ fontSize: 12 }}>🖼</span>
              {sessionLogo ? 'Replace logo…' : 'Upload logo…'}
              <input
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  e.target.value = '';
                  if (!file) return;
                  setSessionLogoErr(null);
                  if (!file.type.startsWith('image/')) { setSessionLogoErr('Please choose an image file.'); return; }
                  if (file.size > 400 * 1024) { setSessionLogoErr('Image too large (max ~300 KB).'); return; }
                  const reader = new FileReader();
                  reader.onload = () => setSessionLogo(reader.result);
                  reader.onerror = () => setSessionLogoErr('Could not read file.');
                  reader.readAsDataURL(file);
                }}
              />
            </label>
            {sessionLogo && (
              <button
                style={{ appearance: 'none', height: 26, padding: '0 10px', border: '.5px solid rgba(0,0,0,.1)',
                  borderRadius: 7, background: 'rgba(0,0,0,.06)', color: 'inherit', font: 'inherit', fontSize: 11,
                  cursor: 'default' }}
                onClick={() => { setSessionLogo(null); setSessionLogoErr(null); }}
              >Reset</button>
            )}
          </div>
          {sessionLogoErr && <div style={{ fontSize: 10.5, color: '#c0392b' }}>{sessionLogoErr}</div>}
          <div style={{ fontSize: 10, color: 'rgba(41,38,27,.4)', lineHeight: 1.4 }}>
            Session only · clears on logout
          </div>
        </div>
      </TweaksPanel>
    </div>
  );
}

// ── Imports manager ──────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const map = {
    completed: 'chip-green',
    processing: 'chip-teal',
    pending: 'chip-teal',
    failed: 'chip-red',
  };
  return <span className={css('chip', map[status] || '')} style={{ textTransform: 'capitalize' }}>{status}</span>;
}

function ImportsView({ batches, uploading, onUpload, onSample, onDelete, onRefresh }) {
  const staffInp = useR();
  const studentInp = useR();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div className="card">
        <div className="card-head">
          <div>
            <div className="card-title">Import web-filter CSV exports</div>
            <div className="card-sub">Files are uploaded, parsed, detected and aggregated on the server. Re-uploading the same file is skipped automatically.</div>
          </div>
          <button className="btn btn-sm" onClick={onRefresh}><Icon name="refresh-cw" /> Refresh</button>
        </div>
        <div className="card-body" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <input ref={staffInp} type="file" accept=".csv" multiple style={{ display: 'none' }} onChange={e => { onUpload(e.target.files, 'staff'); e.target.value = ''; }} />
          <input ref={studentInp} type="file" accept=".csv" multiple style={{ display: 'none' }} onChange={e => { onUpload(e.target.files, 'student'); e.target.value = ''; }} />
          <button className="btn btn-primary" disabled={uploading} onClick={() => staffInp.current?.click()}><Icon name="briefcase" /> Upload staff CSV</button>
          <button className="btn btn-primary" disabled={uploading} onClick={() => studentInp.current?.click()}><Icon name="graduation-cap" /> Upload student CSV</button>
          <button className="btn" disabled={uploading} onClick={onSample}><Icon name="sparkles" /> Load sample data</button>
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <div>
            <div className="card-title">Import batches</div>
            <div className="card-sub">{batches.length} batch{batches.length === 1 ? '' : 'es'} · newest first</div>
          </div>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table className="dt">
            <thead>
              <tr>
                <th>File</th>
                <th>Audience</th>
                <th>Status</th>
                <th style={{ textAlign: 'right' }}>Rows</th>
                <th style={{ textAlign: 'right' }} title="Records actually stored (new, after dropping duplicates)">Stored</th>
                <th style={{ textAlign: 'right' }} title="Records skipped because an identical one was already stored">Duplicates</th>
                <th style={{ textAlign: 'right' }}>Noise</th>
                <th>Imported</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {batches.map(b => (
                <tr key={b.id}>
                  <td><span style={{ fontWeight: 500, color: 'var(--ink)' }}>{b.filename}</span>{b.error && <div style={{ color: 'var(--red, #c0392b)', fontSize: 11, marginTop: 2 }}>{b.error}</div>}</td>
                  <td><span className="chip" style={{ textTransform: 'capitalize' }}>{b.audience}</span></td>
                  <td><StatusBadge status={b.status} /></td>
                  <td className="num">{fmtNum(b.row_count || 0)}</td>
                  <td className="num">{fmtNum(b.event_count || 0)}</td>
                  <td className="num" style={b.duplicate_count ? { color: 'var(--amber-2, #b7791f)', fontWeight: 500 } : { color: 'var(--ink-4)' }}>{fmtNum(b.duplicate_count || 0)}</td>
                  <td className="num">{fmtNum(b.noise_count || 0)}</td>
                  <td><span style={{ fontSize: 12, color: 'var(--ink-3)' }}>{b.created_at ? new Date(b.created_at).toLocaleString('en-GB') : '—'}</span></td>
                  <td style={{ textAlign: 'right' }}>
                    <button className="btn btn-sm btn-ghost" title="Delete batch" onClick={() => onDelete(b.id)}><Icon name="trash-2" /></button>
                  </td>
                </tr>
              ))}
              {batches.length === 0 && (
                <tr><td colSpan={9} style={{ padding: 40, textAlign: 'center', color: 'var(--ink-3)' }}>No imports yet — upload a CSV or load the sample data above.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function UploadFirstScreen({ drag, onDragOver, onDragLeave, onDrop, onUpload, onSample }) {
  const staffInp = useR();
  const studentInp = useR();
  return (
    <div style={{ maxWidth: 720, margin: '40px auto' }}>
      <div
        className={css('dropzone', drag && 'drag')}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
      >
        <div className="dropzone-icon"><Icon name="upload-cloud" /></div>
        <div className="dropzone-title">Upload your web filter exports</div>
        <div className="dropzone-sub">Staff and students each get their own CSV — upload them separately so you can analyse usage by audience. Files are imported and aggregated on the server, then stored durably. <span style={{ color: 'var(--ink-4)' }}>(Dropped files are added as staff.)</span></div>
        <input ref={staffInp} type="file" accept=".csv" multiple style={{ display: 'none' }} onChange={e => { onUpload(e.target.files, 'staff'); e.target.value = ''; }} />
        <input ref={studentInp} type="file" accept=".csv" multiple style={{ display: 'none' }} onChange={e => { onUpload(e.target.files, 'student'); e.target.value = ''; }} />
        <div style={{ marginTop: 22, display: 'inline-flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
          <button className="btn btn-primary" onClick={() => staffInp.current?.click()}><Icon name="briefcase" /> Upload staff CSV</button>
          <button className="btn btn-primary" onClick={() => studentInp.current?.click()}><Icon name="graduation-cap" /> Upload student CSV</button>
          <button className="btn" onClick={onSample}><Icon name="sparkles" /> Load sample data</button>
        </div>
      </div>

      <div style={{ marginTop: 24, display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
        {[
          { i: 'users-round', t: 'Staff vs student', d: 'Two CSVs, two audiences — compare adoption side by side.' },
          { i: 'boxes', t: 'SaaS detection', d: '60+ EdTech & business products mapped from hostnames.' },
          { i: 'school', t: 'Per-school view', d: 'Spot trust-wide products and individual-school outliers.' },
        ].map(c => (
          <div key={c.t} className="card" style={{ padding: 16 }}>
            <div style={{ width: 32, height: 32, borderRadius: 8, background: 'var(--teal-tint)', color: 'var(--teal-2)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 10 }}><Icon name={c.i} /></div>
            <div style={{ fontWeight: 600, fontSize: 13 }}>{c.t}</div>
            <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 4 }}>{c.d}</div>
          </div>
        ))}
      </div>

      <div style={{ marginTop: 18, padding: '10px 14px', background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 10, fontSize: 12, color: 'var(--ink-3)' }}>
        <span className="mono">Expected columns:</span> Date · Type · Reason · Custom Group · Policy · Keyword · Category · Site Name · Path · IP Address · User · Status
      </div>
    </div>
  );
}

// ── Login ────────────────────────────────────────────────────────────────────
function LoginScreen({ onLoggedIn }) {
  const [email, setEmail] = useS('');
  const [password, setPassword] = useS('');
  const [busy, setBusy] = useS(false);
  const [err, setErr] = useS(null);
  // Optional org-branding hint: ?org=<id> in the URL (deep-linked / bookmarked
  // login). Fetch that org's logo from the public endpoint and show it in place
  // of the default cloud mark. Any failure / missing hint falls back silently.
  const [orgLogo, setOrgLogo] = useS(null);
  useE(() => {
    let cancelled = false;
    let orgId = null;
    try { orgId = new URLSearchParams(window.location.search).get('org'); } catch { /* ignore */ }
    const id = Number(orgId);
    if (!orgId || !Number.isInteger(id) || id <= 0) return;
    api.getOrgLogo(id)
      .then(({ logo }) => { if (!cancelled && logo) setOrgLogo(logo); })
      .catch(() => { /* fall back to default logo */ });
    return () => { cancelled = true; };
  }, []);

  async function submit(e) {
    e.preventDefault();
    if (!email.trim() || !password) return;
    setBusy(true); setErr(null);
    try {
      const { user, csrfToken } = await api.login(email.trim(), password);
      setCsrfToken(csrfToken);
      onLoggedIn(user);
    } catch (e2) {
      setErr(e2.status === 401 ? 'Incorrect email or password.' : (e2.message || 'Sign in failed.'));
      setBusy(false);
    }
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)', padding: 20 }}>
      <form onSubmit={submit} className="card" style={{ width: '100%', maxWidth: 380, padding: 28, display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Logo src={orgLogo} />
          <div>
            <div className="brand-name" style={{ fontSize: 18 }}>SaaS Monitor</div>
            <div className="brand-sub">Trust analytics</div>
          </div>
        </div>
        <div>
          <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--ink)' }}>Sign in</div>
          <div style={{ fontSize: 12.5, color: 'var(--ink-3)', marginTop: 2 }}>Accounts are created by an administrator.</div>
        </div>
        {err && <div style={{ background: 'color-mix(in srgb, var(--red) 12%, transparent)', color: 'var(--red)', padding: '8px 12px', borderRadius: 8, fontSize: 12.5 }}>{err}</div>}
        <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>Email</span>
          <input className="input" type="email" autoComplete="username" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@trust.ac.uk" autoFocus />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>Password</span>
          <input className="input" type="password" autoComplete="current-password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" />
        </label>
        <button className="btn btn-primary" type="submit" disabled={busy || !email.trim() || !password}>
          {busy ? <><span className="spinner" style={{ width: 13, height: 13 }} /> Signing in…</> : <><Icon name="log-in" /> Sign in</>}
        </button>
      </form>
    </div>
  );
}

// ── Root / session gate ──────────────────────────────────────────────────────
// On mount we ask the server who we are (GET /me). The session cookie is sent
// automatically (api uses credentials:'include'). If there's a live session the
// server returns the user + a fresh CSRF token; otherwise we show the login
// screen. Logout clears the server session and drops us back to login.
function Root() {
  const [ready, setReady] = useS(false);
  const [user, setUser] = useS(null);

  const loadMe = useC(async () => {
    try {
      const { user, csrfToken } = await api.me();
      setCsrfToken(csrfToken);
      setUser(user);
    } catch {
      setCsrfToken(null);
      setUser(null);
    } finally {
      setReady(true);
    }
  }, []);

  useE(() => { loadMe(); }, [loadMe]);

  const onLogout = useC(async () => {
    try { await api.logout(); } catch { /* ignore */ }
    setCsrfToken(null);
    setActiveOrgId(null);
    try { sessionStorage.removeItem('session_logo'); } catch { /* ignore */ }
    setUser(null);
  }, []);

  if (!ready) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)' }}>
        <Loading label="Loading…" />
      </div>
    );
  }
  if (!user) return <LoginScreen onLoggedIn={setUser} />;
  // Remount the whole app per identity so all per-user state resets cleanly on
  // login/logout/account switch.
  return <App key={user.id} session={user} onLogout={onLogout} />;
}

createRoot(document.getElementById('root')).render(<Root />);
